import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { JepCurrentAccountService } from '@jepto/shared/services/jep-current-account/jep-current-account.service';
import { JepRequestService } from '@jepto/shared/services/jep-request/jep-request.service';
import * as fromSharedAccountStore from '@jepto/shared/store/account/index';
import type {
	IAccount,
	IAccountAiCreditIncentive,
} from '@jepto/shared/store/account/models/account.model';
import { isErrorExistingFromGQLResponse } from '@jepto/shared/utils/collection';
import { error } from '@jepto/shared/utils/logger';

import { AI_CREDITS_INCENTIVE_DEFINITIONS } from '../constants/ai-credits-incentives.config';
import { mutationHideAiCreditIncentive } from '../graphql/mutations/hide-ai-credit-incentive';
import { mutationSubmitAiCreditIncentive } from '../graphql/mutations/submit-ai-credit-incentive';
import type {
	AiCreditsIncentiveId,
	AiCreditsIncentiveItem,
	AiCreditsIncentiveStatus,
	AiCreditsIncentiveVerification,
	SubmitAiCreditIncentiveInput,
	SubmitAiCreditIncentiveResult,
} from '../models/ai-credits-incentive.model';

type ApiIncentiveRow = IAccountAiCreditIncentive;

type AccountIncentiveSnapshot = Pick<
	IAccount,
	| 'name'
	| 'domain'
	| 'timezone'
	| 'weekStart'
	| 'sortKey'
	| 'revisions'
	| 'aiCreditIncentives'
>;

const SUBMIT_FAILED_MESSAGE = 'Could not submit incentive. Please try again.';

@Injectable({ providedIn: 'root' })
export class AiCreditsIncentiveService {
	private readonly requestService = inject(JepRequestService);
	private readonly currentAccount = inject(JepCurrentAccountService);
	private readonly store = inject(Store);

	#hideIncentive = false;
	#accountSnapshot: AccountIncentiveSnapshot | null = null;

	isHideIncentive(): boolean {
		return this.#hideIncentive;
	}

	async listIncentives(): Promise<AiCreditsIncentiveItem[]> {
		const statusById = await this.#statusesFromAccountStore();

		return AI_CREDITS_INCENTIVE_DEFINITIONS.map(definition => {
			const apiRow = statusById.get(definition.id);
			return {
				...definition,
				status: apiRow
					? this.#normalizeStatus(apiRow.status)
					: 'available',
				...this.#verificationFrom(apiRow),
			};
		});
	}

	async submitIncentive(
		input: SubmitAiCreditIncentiveInput
	): Promise<SubmitAiCreditIncentiveResult> {
		const response = await firstValueFrom(
			this.requestService.mutate({
				mutation: mutationSubmitAiCreditIncentive,
				variables: {
					input: {
						id: input.incentiveId,
						proofUrl: input.proofUrl.trim(),
					},
				},
			})
		);

		if (isErrorExistingFromGQLResponse(response)) {
			// Validation errors from the resolver are written for the customer.
			throw new Error(
				response?.errors?.[0]?.message || SUBMIT_FAILED_MESSAGE
			);
		}

		const result = response?.data?.submitAiCreditIncentive as
			| ApiIncentiveRow
			| undefined;

		if (!result?.id) {
			throw new Error(SUBMIT_FAILED_MESSAGE);
		}

		return {
			id: result.id as AiCreditsIncentiveId,
			status: this.#normalizeStatus(result.status),
			...this.#verificationFrom(result),
			monthlyCredits:
				result.monthlyCredits ??
				AI_CREDITS_INCENTIVE_DEFINITIONS.find(
					item => item.id === result.id
				)?.monthlyCredits ??
				0,
		};
	}

	async dismissIncentivesPanel(): Promise<void> {
		const account = this.#accountSnapshot;
		if (!account) {
			throw new Error(
				'Account details are not loaded yet. Open incentives again and retry.'
			);
		}

		const response = await firstValueFrom(
			this.requestService.mutate({
				mutation: mutationHideAiCreditIncentive,
				variables: {
					input: {
						hideIncentive: true,
						name: account.name,
						domain: account.domain,
						timezone: account.timezone,
						weekStart: account.weekStart,
						sortKey: account.sortKey ?? 'account#v0',
						revisions: account.revisions ?? 0,
						userKey: this.currentAccount.getCognitoUserId(),
					},
				},
			})
		);

		if (isErrorExistingFromGQLResponse(response)) {
			throw new Error(
				response?.errors?.[0]?.message ||
					'Could not hide the incentives panel.'
			);
		}

		this.#hideIncentive = true;
	}

	/**
	 * Incentive rows and dismiss/editAccount fields come from the shared account
	 * getItem query — not a second `listAiCreditIncentives` getItem round-trip.
	 */
	async #statusesFromAccountStore(): Promise<
		Map<AiCreditsIncentiveId, ApiIncentiveRow>
	> {
		try {
			const account = await firstValueFrom(
				this.store
					.select(fromSharedAccountStore.getAccountData)
					.pipe(
						filter((value): value is IAccount => value != null),
						take(1)
					)
			);

			this.#accountSnapshot = {
				name: account.name,
				domain: account.domain,
				timezone: account.timezone,
				weekStart: account.weekStart,
				sortKey: account.sortKey,
				revisions: account.revisions,
				aiCreditIncentives: account.aiCreditIncentives,
			};
			// Account.hideIncentive is not on the live schema yet; dismiss is
			// session-local via #hideIncentive / the incentive store until it is.
			this.#hideIncentive =
				this.#hideIncentive || account.hideIncentive === true;

			const rows = (account.aiCreditIncentives ??
				[]) as ApiIncentiveRow[];

			return rows.reduce((map, row) => {
				if (this.#isKnownIncentiveId(row.id)) {
					map.set(row.id, {
						...row,
						status: this.#normalizeStatus(row.status),
					});
				}
				return map;
			}, new Map<AiCreditsIncentiveId, ApiIncentiveRow>());
		} catch (err) {
			error('AiCreditsIncentiveService.listIncentives', err);
			return new Map();
		}
	}

	#verificationFrom(
		row: ApiIncentiveRow | undefined
	): AiCreditsIncentiveVerification {
		return {
			proofUrl: row?.proofUrl ?? null,
			lastVerifiedAt: row?.lastVerifiedAt ?? null,
			statusMessage: row?.statusMessage ?? null,
			rejectionCode: row?.rejectionCode ?? null,
			rejectionReason: row?.rejectionReason ?? null,
		};
	}

	#isKnownIncentiveId(id: string): id is AiCreditsIncentiveId {
		return AI_CREDITS_INCENTIVE_DEFINITIONS.some(item => item.id === id);
	}

	#normalizeStatus(status: string): AiCreditsIncentiveStatus {
		switch (status) {
			case 'pending':
			case 'verified':
			case 'rejected':
				return status;
			default:
				return 'available';
		}
	}
}
